import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import CustomWidgets, { getWidgetDetails } from "../Home/CustomWidgets";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import { useContext, useEffect, useState } from "react";
import { formatDate } from "../../services/util";
import { SettingContext } from "../../routes";
import moment from "moment";
import { Details } from "../Details/Details";
import { Modal } from "antd";

const dataChartFields = [
    { name: "Cash Collected", key: "cash_collected" },
    { name: "Cash Dispensed", key: "cash_dispence" },
    { name: "Total Cash", key: "total_cash" },
];

const COLORS = [
    "hsl(199, 89%, 48%)",
    "hsl(142, 71%, 45%)",
    "hsl(38, 92%, 50%)",
    "hsl(280, 65%, 60%)",
];

const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
        return (
            <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                <p className="text-sm text-muted-foreground">
                    {payload[0].name}
                </p>
                <p className="text-sm font-semibold text-foreground">
                    ${payload[0].value.toLocaleString()}
                </p>
            </div>
        );
    }
    return null;
};

const EmptyState = () => {
    return (
        <div className="flex min-h-[220px] items-center justify-center rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-5 text-center">
            <div>
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-primary-light)]">
                    <span className="h-3 w-3 rounded-full bg-[var(--color-primary)]" />
                </div>
                <h4 className="m-0 text-base font-semibold text-[var(--color-text)]">
                    No revenue data
                </h4>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-[var(--color-text-secondary)]">
                    There are no cash collection or cash dispense records for
                    the selected date range or hotel filters.
                </p>
            </div>
        </div>
    );
};

export function RevenueBreakdown() {
    const [dataChart, setData] = useState([]);
    const [visibleModalFor, setVisibleModalFor] = useState(null);
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const {
        selectedDates,
        selectedHotels,
        selectedHotelType,
        selectedHotelActiveType,
        kioskSelection,
    } = useContext(SettingContext);
    const hotel_ids = selectedHotels;
    const [start_date, end_date] = selectedDates;
    const diff = moment(end_date).diff(moment(start_date), "days");
    const previous_start_date = moment(start_date).subtract(
        diff === 0 ? 1 : diff,
        "days",
    );
    const previous_end_date = moment(start_date).subtract(1, "days");
    const [defaultFormData, setDefaultFormData] = useState({
        staff_id,
        hotel_ids: hotel_ids,
        all_selected: hotel_ids.length > 0 ? false : true,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
        previous_start_date: formatDate(previous_start_date),
        previous_end_date: formatDate(previous_end_date),
        hotel_type: selectedHotelType,
        hotel_active_type: selectedHotelActiveType,
        ...(selectedHotelType == "kiosk"
            ? { calling_hotel_type: kioskSelection }
            : {}),
    });
    const {
        data: chartData,
        status,
        refetch,
    } = useQuery(
        "cashCount",
        async () => {
            return await HTTP("/dashboard/getDashboardCashCounts", {
                ...defaultFormData,
            });
        },
        {
            enabled: false,
        },
    );

    useEffect(() => {
        refetch();
    }, [defaultFormData]);

    useEffect(() => {
        if (status === "success" && chartData) {
            const formattedData = dataChartFields.map((field) => {
                if (chartData.data[field.key]) {
                    return {
                        name: field.name,
                        field: field.key,
                        value: Number(chartData.data[field.key]),
                    };
                } else {
                    return {
                        name: field.name,
                        value: 0,
                    };
                }
            });

            setData(formattedData);
        }
    }, [status, chartData]);

    const total = dataChart.reduce((sum, d) => sum + d.value, 0);

    return (
        <div className="chart-container animate-fade-in">
            <h3 className="section-title font-semibold text-foreground mb-1">
                Revenue Breakdown
            </h3>
            <p className="section-subtitle mb-4 text-gray-500">
                By payment method
            </p>
            {total > 0 ? (
                <div className="flex flex-col sm:flex-row items-center gap-6">
                    <ResponsiveContainer width={160} height={160}>
                        <PieChart>
                            <Pie
                                data={dataChart}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={72}
                                paddingAngle={3}
                                dataKey="value"
                                strokeWidth={0}
                            >
                                {dataChart.map((_, index) => (
                                    <Cell
                                        cursor={"pointer"}
                                        key={index}
                                        fill={COLORS[index]}
                                        onClick={() =>
                                            setVisibleModalFor(
                                                dataChart[index].field,
                                            )
                                        }
                                    />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="flex-1 space-y-3">
                        {dataChart.map((item, index) => {
                            const percentage =
                                total > 0
                                    ? ((item.value / total) * 100).toFixed(0)
                                    : "0";

                            return (
                                <div
                                    key={item.name}
                                    className="flex items-center justify-between"
                                >
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="w-2.5 h-2.5 rounded-full"
                                            style={{
                                                backgroundColor: COLORS[index],
                                            }}
                                        />
                                        <span className="text-sm text-muted-foreground">
                                            {item.name}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-sm font-medium text-foreground">
                                            ${item.value.toLocaleString()}
                                        </span>
                                        <span className="text-xs text-muted-foreground ml-2">
                                            {percentage}%
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <EmptyState />
            )}
            <Modal
                open={visibleModalFor !== null}
                title={`Revenue Breakdown Details for ${
                    dataChartFields.find((f) => f.key === visibleModalFor)
                        ?.name || ""
                }`}
                onCancel={() => setVisibleModalFor(null)}
                width={1200}
                footer={null}
                destroyOnHidden
            >
                <Details isModel={true} fieldKey={visibleModalFor} />
            </Modal>
        </div>
    );
}
